import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import Icons from '../../assets/icons';
import Hooks from '../../hooks';
import Components from '..';
import './DatePickerDropdown.scss';

interface Props {
  name: string;
  value?: string | null; // YYYY-MM-DD
  onChange: (isoYmd: string) => void;
  minDate?: string | null; // YYYY-MM-DD
  maxDate?: string | null; // YYYY-MM-DD
  disabled?: boolean;
  placeholder?: string;
}

export default function DatePickerDropdown({
  name,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  placeholder = 'Wybierz datę',
}: Props) {
  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string | null>(value ?? null);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const ref = Hooks.useOutsideClick({
    shouldWatch: open,
    callback: () => setOpen(false),
  });

  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const updatePosition = () => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    setPos({
      top: r.bottom + 8,
      left: r.left + r.width / 2,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  const handleSelect = (isoAny: string) => {
    const isoYmd = moment(isoAny).format('YYYY-MM-DD');
    setInternal(isoYmd);
    onChange(isoYmd);
    setOpen(false);
  };

  return (
    <div className="berg-components-date-dropdown__period-wrapper" ref={ref}>
      <div
        className="berg-components-date-dropdown__period"
        onClick={() => !disabled && setOpen((p) => !p)}
        role="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-disabled={disabled}
        ref={anchorRef}
      >
        <Icons.Calendar />
        <span className="berg-components-date-dropdown__period-dates">
          {internal ? moment(internal).format('DD.MM.YYYY') : placeholder}
        </span>
        <div
          className={
            'berg-components-date-dropdown__period-arrow' +
            (open ? ' is-open' : '')
          }
        />
      </div>

      {/* portal */}
      {open &&
        !disabled &&
        createPortal(
          <div
            className="berg-components-date-dropdown__calendar-portal"
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              transform: 'translateX(-50%)',
              zIndex: 10000,
            }}
          >
            <div className="berg-components-date-dropdown__calendar">
              <Components.SingleCalendar
                dateChoice={internal}
                onDateChange={handleSelect}
                minDateISO={minDate ?? null}
                maxDateISO={maxDate ?? null}
                hideNavigation
              />
            </div>
          </div>,
          document.body
        )}

      <input type="hidden" name={name} value={internal ?? ''} readOnly />
    </div>
  );
}
